import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  BarChart3, Star, Settings as SettingsIcon, UserCog,
  FileSearch, FileText, Briefcase, Search, Trash2, MapPin, Tag, X, HelpCircle,
  Users2, Ticket, Contact2, Layers,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from "recharts";
import CrmPanel from "@/components/crm/CrmPanel";
import CouponBatches from "@/components/crm/CouponBatches";
import CouponAnalytics from "@/components/crm/CouponAnalytics";

// ─── Status badge helper ───────────────────────────────────────────────
const STATUS_STYLES = {
  approved: "bg-green-100 text-green-700 border-green-200",
  pending:  "bg-yellow-100 text-yellow-700 border-yellow-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  expired:  "bg-slate-100 text-slate-500 border-slate-200",
};
function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[status] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
      {status}
    </span>
  );
}

// ─── Analytics ────────────────────────────────────────────────────────
function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/superadmin/analytics").then(r => setData(r.data)); }, []);
  if (!data) return <div className="skeleton h-64 rounded-xl" />;
  return (
    <div data-testid="analytics-page">
      <h2 className="text-2xl font-bold mb-6">Platform Analytics</h2>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Posters</div><div className="text-3xl font-bold">{data.posters}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Seekers</div><div className="text-3xl font-bold">{data.seekers}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Applications</div><div className="text-3xl font-bold">{data.applications}</div></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <h3 className="font-semibold mb-4">Jobs posted (last 30 days)</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data.by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#000" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold mb-4">Top Cities</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.top_cities}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#000" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold mb-4">Top Categories</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.top_categories}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#404040" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6">
        <CouponAnalytics />
      </div>
    </div>
  );
}
const JOB_STATUSES = ["all", "approved", "pending", "rejected", "expired"];
const JOB_TYPES    = ["Full-time","Part-time","Daily Wage","Contract","Internship","Walk-in"];

function JobPosts() {
  const [jobs, setJobs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [deleting, setDeleting]   = useState(null); // job id being deleted

  // filters
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [typeFilter, setType]     = useState("all");
  const [page, setPage]           = useState(1);
  const LIMIT = 20;

  // debounced search
  const searchTimer = useRef(null);
  const [debouncedQ, setDebouncedQ] = useState("");
  const handleSearchChange = (v) => {
    setSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedQ(v); setPage(1); }, 400);
  };

  const fetchJobs = () => {
    setLoading(true);
    const params = { page, limit: LIMIT, sort: "newest" };
    if (debouncedQ)               params.q        = debouncedQ;
    if (statusFilter !== "all")   params.status   = statusFilter;
    if (typeFilter   !== "all")   params.job_type = typeFilter;
    // Use admin endpoint so we see ALL statuses
    api.get("/superadmin/all-jobs", { params })
      .then(r => { setJobs(r.data.items); setTotal(r.data.total); })
      .catch(() => {
        // Fallback: use public /jobs but pass status param (backend supports it via admin role)
        api.get("/admin/all-jobs", { params })
          .then(r => { setJobs(r.data.items); setTotal(r.data.total); })
          .catch(() => {
            // Last fallback: fetch approved + pending separately and merge client-side
            Promise.all([
              api.get("/jobs", { params: { ...params, limit: 200 } }),
              api.get("/admin/jobs/pending"),
            ]).then(([approvedRes, pendingRes]) => {
              const approved = approvedRes.data.items || [];
              const pending  = pendingRes.data || [];
              const merged   = [...pending, ...approved.filter(j => !pending.find(p => p.id === j.id))];
              const filtered = merged
                .filter(j => statusFilter === "all" || j.status === statusFilter)
                .filter(j => typeFilter   === "all" || j.job_type === typeFilter)
                .filter(j => !debouncedQ  || j.title.toLowerCase().includes(debouncedQ.toLowerCase()) ||
                              j.business_name?.toLowerCase().includes(debouncedQ.toLowerCase()));
              setJobs(filtered.slice((page - 1) * LIMIT, page * LIMIT));
              setTotal(filtered.length);
            });
          });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchJobs(); }, [debouncedQ, statusFilter, typeFilter, page]);

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete "${job.title}" by ${job.business_name}? This cannot be undone.`)) return;
    setDeleting(job.id);
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job deleted successfully");
      fetchJobs();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setDeleting(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const activeFilters = [
    debouncedQ && `"${debouncedQ}"`,
    statusFilter !== "all" && statusFilter,
    typeFilter   !== "all" && typeFilter,
  ].filter(Boolean);

  return (
    <div data-testid="job-posts-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold">All Job Posts</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {loading ? "Loading…" : `${total} job${total !== 1 ? "s" : ""} total`}
            {activeFilters.length > 0 && <span className="ml-1">· filtered by {activeFilters.join(", ")}</span>}
          </p>
        </div>
      </div>

      {/* Filters row */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        {/* Keyword */}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search title, company…"
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            className="pl-9"
            data-testid="job-search-input"
          />
          {search && (
            <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black"
              onClick={() => { setSearch(""); setDebouncedQ(""); setPage(1); }}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Status */}
        <Select value={statusFilter} onValueChange={v => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {JOB_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Job type */}
        <Select value={typeFilter} onValueChange={v => { setType(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="type-filter">
            <SelectValue placeholder="Job type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {JOB_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Clear */}
        {(statusFilter !== "all" || typeFilter !== "all" || debouncedQ) && (
          <button
            className="text-xs text-slate-500 hover:text-black underline whitespace-nowrap"
            onClick={() => { setSearch(""); setDebouncedQ(""); setStatus("all"); setType("all"); setPage(1); }}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-10 rounded-lg" />)}
          </div>
        ) : jobs.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <Briefcase className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium text-slate-700">No jobs found</p>
            <p className="text-sm mt-1">Try adjusting your filters.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">Job Title</TableHead>
                <TableHead className="font-semibold text-slate-700">Company</TableHead>
                <TableHead className="font-semibold text-slate-700">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />City</span>
                </TableHead>
                <TableHead className="font-semibold text-slate-700">
                  <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" />Category</span>
                </TableHead>
                <TableHead className="font-semibold text-slate-700">Type</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">Posted</TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map(j => (
                <TableRow key={j.id} className="hover:bg-slate-50 transition-colors" data-testid={`job-row-${j.id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900 leading-tight">{j.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {j.vacancies > 1 && `${j.vacancies} vacancies · `}
                      {j.views ?? 0} views · {j.applications_count ?? 0} applied
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-[140px] truncate">{j.business_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{j.city}</TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-[120px] truncate">{j.category}</TableCell>
                  <TableCell>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200">
                      {j.job_type}
                    </span>
                  </TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell className="text-xs text-slate-400 text-right whitespace-nowrap">
                    {(j.created_at || "").slice(0, 10)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 gap-1.5"
                      onClick={() => handleDelete(j)}
                      disabled={deleting === j.id}
                      data-testid={`delete-job-${j.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {deleting === j.id ? "Deleting…" : "Delete"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">
            Page {page} of {totalPages} · {total} total
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admins ────────────────────────────────────────────────────────────
function Admins() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", assigned_city: "all" });
  const refresh = () => api.get("/superadmin/admins").then(r => setAdmins(r.data));
  useEffect(() => { refresh(); }, []);
  const add = async () => {
    try { await api.post("/superadmin/admins", form); setForm({ name: "", email: "", password: "", assigned_city: "all" }); toast.success("Admin created"); refresh(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const remove = async (id) => { if (window.confirm("Remove admin?")) { await api.delete(`/superadmin/admins/${id}`); refresh(); } };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Admin Management</h2>
      <div className="bg-white border border-slate-200 rounded-xl p-4 grid md:grid-cols-5 gap-2 mb-4">
        <Input placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
        <Input placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
        <Input type="password" placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
        <Input placeholder="City (or all)" value={form.assigned_city} onChange={e => setForm({...form, assigned_city: e.target.value})} />
        <Button onClick={add} className="bg-black hover:bg-slate-800 text-white" data-testid="add-admin-btn">Create Admin</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Assigned</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {admins.map(a => (
              <TableRow key={a.id}>
                <TableCell>{a.name}</TableCell>
                <TableCell>{a.email}</TableCell>
                <TableCell>{a.assigned_city || "all"}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => remove(a.id)}>Remove</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Featured ──────────────────────────────────────────────────────────
function Featured() {
  const [jobs, setJobs] = useState([]);
  const refresh = () => api.get("/jobs?limit=50").then(r => setJobs(r.data.items));
  useEffect(() => { refresh(); }, []);
  const toggle = async (j) => {
    await api.post(`/superadmin/jobs/${j.id}/feature`, null, { params: { featured: !j.featured } });
    refresh();
  };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Featured Jobs</h2>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>City</TableHead><TableHead>Featured</TableHead></TableRow></TableHeader>
          <TableBody>
            {jobs.map(j => (
              <TableRow key={j.id}>
                <TableCell>{j.title} <span className="text-xs text-slate-500">- {j.business_name}</span></TableCell>
                <TableCell>{j.city}</TableCell>
                <TableCell><Switch checked={!!j.featured} onCheckedChange={() => toggle(j)} data-testid={`feature-toggle-${j.id}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Platform Settings ─────────────────────────────────────────────────
function PlatformSettings() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/superadmin/settings").then(r => setS(r.data || {})); }, []);
  const save = async () => { try { await api.put("/superadmin/settings", s); toast.success("Saved"); } catch (e) { toast.error(formatError(e)); } };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Platform Settings</h2>
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div><Label>Site Name</Label><Input value={s.site_name || ""} onChange={e => setS({...s, site_name: e.target.value})} /></div>
        <div><Label>Contact Email</Label><Input value={s.contact_email || ""} onChange={e => setS({...s, contact_email: e.target.value})} /></div>
        <div><Label>Hero Headline</Label><Input value={s.hero_text || ""} onChange={e => setS({...s, hero_text: e.target.value})} /></div>
        <div><Label>Announcement Banner</Label><Textarea value={s.announcement || ""} onChange={e => setS({...s, announcement: e.target.value})} /></div>
        <div><Label>Max Jobs / Poster / Month</Label><Input type="number" value={s.max_jobs_per_month || 50} onChange={e => setS({...s, max_jobs_per_month: parseInt(e.target.value)})} /></div>
        <div className="flex items-center justify-between border-t pt-4">
          <Label>Maintenance Mode</Label>
          <Switch checked={!!s.maintenance_mode} onCheckedChange={v => setS({...s, maintenance_mode: v})} />
        </div>
        <div className="flex items-center justify-between">
          <Label>Auto-approve new jobs (skip review)</Label>
          <Switch checked={!!s.auto_approval} onCheckedChange={v => setS({...s, auto_approval: v})} data-testid="auto-approve-switch" />
        </div>
        <Button onClick={save} className="bg-black hover:bg-slate-800 text-white" data-testid="save-settings-btn">Save Settings</Button>
      </div>
    </div>
  );
}

// ─── Consents ──────────────────────────────────────────────────────────
function ConsentsAdmin() {
  const [role, setRole] = useState("job_seeker");
  const [items, setItems] = useState({});
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const refresh = () => api.get("/consents").then(r => setItems(r.data || {}));
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    const c = items[role] || {};
    setTitle(c.title || ""); setBody(c.body || "");
  }, [role, items]);
  const save = async () => {
    try {
      await api.put("/consents", { role, title, body });
      toast.success("Consent updated — new users will see this version");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };
  const current = items[role] || {};
  return (
    <div data-testid="consents-admin">
      <h2 className="text-2xl font-bold mb-2">Consent Letters</h2>
      <p className="text-slate-500 text-sm mb-6">Manage the consent terms that users must accept on signup. Each role has its own consent.</p>
      <div className="flex gap-2 mb-4 items-center">
        <Button size="sm" variant={role === "job_seeker" ? "default" : "outline"} onClick={() => setRole("job_seeker")} className={role === "job_seeker" ? "bg-black hover:bg-slate-800" : ""} data-testid="consent-role-seeker">Job Seeker</Button>
        <Button size="sm" variant={role === "job_poster" ? "default" : "outline"} onClick={() => setRole("job_poster")} className={role === "job_poster" ? "bg-black hover:bg-slate-800" : ""} data-testid="consent-role-poster">Job Poster (Company)</Button>
        {current.version && <span className="ml-auto text-xs text-slate-500">Current: v{current.version} · {(current.updated_at || "").slice(0,10)}</span>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div>
          <Label>Title</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="consent-title-input" />
        </div>
        <div>
          <Label>Body (plain text, line breaks preserved)</Label>
          <Textarea rows={16} value={body} onChange={e => setBody(e.target.value)} className="font-mono text-xs" data-testid="consent-body-input" />
        </div>
        <Button onClick={save} className="bg-black hover:bg-slate-800 text-white" data-testid="save-consent-btn">
          Save Consent (version will increment)
        </Button>
      </div>
    </div>
  );
}

// ─── FAQs ──────────────────────────────────────────────────────────────
function FaqAdmin() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({ category: "", question: "", answer: "", order: 0 });
  const [editingId, setEditingId] = useState(null);

  const refresh = () => api.get("/faqs").then(r => setFaqs(r.data));
  useEffect(() => { refresh(); }, []);

  const grouped = faqs.reduce((acc, f) => { (acc[f.category] = acc[f.category] || []).push(f); return acc; }, {});

  const resetForm = () => { setForm({ category: "", question: "", answer: "", order: 0 }); setEditingId(null); };

  const save = async () => {
    if (!form.category || !form.question || !form.answer) {
      toast.error("Category, question, and answer are all required");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/admin/faqs/${editingId}`, form);
        toast.success("FAQ updated");
      } else {
        await api.post("/admin/faqs", form);
        toast.success("FAQ added");
      }
      resetForm();
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const edit = (f) => {
    setForm({ category: f.category, question: f.question, answer: f.answer, order: f.order || 0 });
    setEditingId(f.id);
  };

  const remove = async (id) => {
    await api.delete(`/admin/faqs/${id}`);
    if (editingId === id) resetForm();
    refresh();
  };

  const removeCategory = async (category) => {
    if (!window.confirm(`Delete the entire "${category}" section and all its questions?`)) return;
    await api.delete(`/admin/faqs/by-category/${encodeURIComponent(category)}`);
    if (form.category === category) resetForm();
    refresh();
  };

  return (
    <div data-testid="faq-admin">
      <h2 className="text-2xl font-bold mb-2">FAQs</h2>
      <p className="text-slate-500 text-sm mb-6">Manage the categorized FAQ sections shown on the Contact page.</p>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 mb-6">
        <h3 className="font-semibold text-sm">{editingId ? "Edit FAQ" : "Add FAQ"}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g. For Employers" data-testid="faq-category-input" />
          </div>
          <div>
            <Label>Order (lower shows first within its category)</Label>
            <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value || "0") })} />
          </div>
        </div>
        <div><Label>Question</Label><Input value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} data-testid="faq-question-input" /></div>
        <div><Label>Answer</Label><Textarea rows={3} value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer-input" /></div>
        <div className="flex gap-2">
          <Button onClick={save} className="bg-black hover:bg-slate-800 text-white" data-testid="save-faq-btn">
            {editingId ? "Update FAQ" : "Add FAQ"}
          </Button>
          {editingId && <Button variant="outline" onClick={resetForm}>Cancel</Button>}
        </div>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <p className="text-sm text-slate-400">No FAQs yet.</p>
      ) : Object.entries(grouped).map(([category, items]) => (
        <div key={category} className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{category}</h3>
            <button
              type="button"
              onClick={() => removeCategory(category)}
              className="text-xs text-slate-400 hover:text-red-600 flex items-center gap-1"
              data-testid={`delete-category-${category}`}
            >
              <Trash2 className="w-3 h-3" /> Delete section
            </button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {items.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map(f => (
              <div key={f.id} className="p-4 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-sm text-slate-900">{f.question}</div>
                  <div className="text-xs text-slate-500 mt-1">{f.answer}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => edit(f)} data-testid={`edit-faq-${f.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="text-red-600" data-testid={`delete-faq-${f.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Audit ─────────────────────────────────────────────────────────────
function Audit() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/superadmin/audit").then(r => setItems(r.data)); }, []);
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Audit Log</h2>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">No activity yet.</TableCell></TableRow>}
            {items.map(a => (
              <TableRow key={a.id}>
                <TableCell className="text-sm">{(a.created_at || "").slice(0,16).replace("T", " ")}</TableCell>
                <TableCell>{a.actor_email}</TableCell>
                <TableCell><Badge variant="outline">{a.action}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{a.target}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Shell ─────────────────────────────────────────────────────────────
export default function SuperAdminDashboard() {
  const location = useLocation();
  const tab = (p) => location.pathname === p
    ? "bg-slate-100 text-black font-semibold"
    : "text-slate-600 hover:bg-slate-50 hover:text-black";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-[240px_1fr] gap-6" data-testid="superadmin-dashboard">
      <aside className="md:sticky md:top-20 md:self-start">
        <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-0.5 text-sm">
          <Link to="/superadmin"          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin")}`}>
            <BarChart3 className="w-4 h-4 shrink-0" />Analytics
          </Link>
          <Link to="/superadmin/jobs"     className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/jobs")}`} data-testid="tab-jobs">
            <Briefcase className="w-4 h-4 shrink-0" />Job Posts
          </Link>
          <Link to="/superadmin/admins"   className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/admins")}`} data-testid="tab-admins">
            <UserCog className="w-4 h-4 shrink-0" />Admins
          </Link>
          <Link to="/superadmin/featured" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/featured")}`} data-testid="tab-featured">
            <Star className="w-4 h-4 shrink-0" />Featured
          </Link>
          <Link to="/superadmin/settings" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/settings")}`} data-testid="tab-settings">
            <SettingsIcon className="w-4 h-4 shrink-0" />Settings
          </Link>
          <Link to="/superadmin/audit"    className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/audit")}`} data-testid="tab-audit">
            <FileSearch className="w-4 h-4 shrink-0" />Audit Log
          </Link>
          <Link to="/superadmin/consents" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/consents")}`} data-testid="tab-consents">
            <FileText className="w-4 h-4 shrink-0" />Consents
          </Link>
          <Link to="/superadmin/faqs" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/faqs")}`} data-testid="tab-faqs">
            <HelpCircle className="w-4 h-4 shrink-0" />FAQs
          </Link>
          <div className="border-t my-2" />
          <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">CRM</div>
          <Link to="/superadmin/crm/newfeed" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/crm/newfeed")}`} data-testid="tab-crm-newfeed">
            <Users2 className="w-4 h-4 shrink-0" />New Feed CRM
          </Link>
          <Link to="/superadmin/crm/coupon" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/crm/coupon")}`} data-testid="tab-crm-coupon">
            <Ticket className="w-4 h-4 shrink-0" />Coupon Code CRM
          </Link>
          <Link to="/superadmin/crm/normal" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/crm/normal")}`} data-testid="tab-crm-normal">
            <Contact2 className="w-4 h-4 shrink-0" />Normal CRM
          </Link>
          <Link to="/superadmin/coupon-batches" className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tab("/superadmin/coupon-batches")}`} data-testid="tab-coupon-batches">
            <Layers className="w-4 h-4 shrink-0" />Coupon Batches
          </Link>
          <div className="border-t my-2" />
          <Link to="/admin" className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-black">
            → Admin Panel
          </Link>
        </div>
      </aside>

      <Routes>
        <Route index          element={<Analytics />} />
        <Route path="jobs"    element={<JobPosts />} />
        <Route path="admins"  element={<Admins />} />
        <Route path="featured"element={<Featured />} />
        <Route path="settings"element={<PlatformSettings />} />
        <Route path="audit"   element={<Audit />} />
        <Route path="consents"element={<ConsentsAdmin />} />
        <Route path="faqs"    element={<FaqAdmin />} />
        <Route path="crm/newfeed" element={<CrmPanel mode="newfeed" />} />
        <Route path="crm/coupon"  element={<CrmPanel mode="coupon" />} />
        <Route path="crm/normal"  element={<CrmPanel mode="normal" />} />
        <Route path="coupon-batches" element={<CouponBatches />} />
      </Routes>
    </div>
  );
}